import {environment} from '@env/environment';

export type NombrePlantilla = 'teki' | 'cbet';

// Nombre comercial que se muestra en los textos de la app (Teki, Cbetcloud, Wariqueando...)
export const MARCA: string = environment.company || 'Teki';

export interface ConfigLogin {
    titulo: string;
    subtitulo: string;
    // Frase del panel de marca
    lema: string;
    ventajas: string[];
    // false cuando el logo ya incluye el nombre de la marca
    mostrarNombre: boolean;
}

export interface ConfigPlantilla {
    // Variables del antiguo tema PrimeNG (<link id="theme-css">), en claro y en oscuro. Los componentes los pinta
    // el preset de config/tema-primeng.ts; estas hojas solo mantienen las variables (--primary-color...) que usan los estilos
    themeCss: string;
    themeCssOscuro: string;
    // CSS del layout de la plantilla (<link id="layout-css">), en claro y en oscuro
    layoutCss: string;
    layoutCssOscuro: string;
    // Hojas extra que la plantilla necesita después del layout
    estilosExtra: string[];
    favicon: string;
    // Nombre corto para el título de la pestaña y el login (CBET usa "CBET" aunque su company sea "Cbetcloud")
    nombre: string;
    // Complemento del título de la pestaña: "<nombre> - <subtitulo>"
    subtitulo: string;
    // Logo para fondos claros (p. ej. la consulta pública)
    logoFondoClaro: string;
    login: ConfigLogin;
}

const PLANTILLAS: Record<NombrePlantilla, ConfigPlantilla> = {
    // Mirage
    teki: {
        themeCss: 'assets/theme/blue/variables-light.css',
        themeCssOscuro: 'assets/theme/blue/variables-dark.css',
        layoutCss: 'assets/layout/css/blue/layout-light.css',
        layoutCssOscuro: 'assets/layout/css/blue/layout-dark.css',
        estilosExtra: [],
        favicon: 'assets/layout/images/favicon.png',
        nombre: MARCA,
        subtitulo: 'Soluciones',
        // isotipo de Teki (la nube, sin el texto): el nombre se escribe al lado (login.mostrarNombre)
        logoFondoClaro: 'assets/layout/images/favicon.png',
        login: {
            titulo: 'Inicia sesión',
            subtitulo: 'Bienvenido de nuevo. Ingresa tu usuario y contraseña para comenzar.',
            lema: 'Facturación electrónica simple y rápida para tu negocio',
            ventajas: ['Válido por SUNAT', '1,000+ negocios en Perú'],
            mostrarNombre: true
        }
    },
    // Avalon
    cbet: {
        themeCss: 'assets/plantillas/cbet/theme/blue/variables-light.css',
        themeCssOscuro: 'assets/plantillas/cbet/theme/blue/variables-dark.css',
        // el layout de Avalon toma los colores del tema: es el mismo en claro y en oscuro
        layoutCss: 'assets/plantillas/cbet/layout/css/layout-blue.css',
        layoutCssOscuro: 'assets/plantillas/cbet/layout/css/layout-blue.css',
        estilosExtra: [
            'assets/plantillas/cbet/layout/styles/layout/layout.css',
            // tipografía del login de CBET
            'https://fonts.googleapis.com/css2?family=Noto+Sans:wght@400;500;600;700;800&display=swap'
        ],
        favicon: 'assets/plantillas/cbet/layout/images/favicon.png',
        nombre: 'CBET',
        subtitulo: 'Punto de venta',
        logoFondoClaro: 'assets/plantillas/cbet/layout/images/logo-dark.png',
        login: {
            titulo: 'Bienvenido a CBET',
            subtitulo: 'Ingresa tu usuario y contraseña para comenzar.',
            lema: 'Comandas, facturación e inventarios en un solo lugar',
            ventajas: ['Facturación electrónica ilimitada', 'Soporte presencial incluido'],
            mostrarNombre: false
        }
    }
};

export const NOMBRE_PLANTILLA: NombrePlantilla = environment.plantilla === 'cbet' ? 'cbet' : 'teki';

export const PLANTILLA: ConfigPlantilla = PLANTILLAS[NOMBRE_PLANTILLA];

export const ES_PLANTILLA_CBET = NOMBRE_PLANTILLA === 'cbet';

export const TITULO_APP = `${PLANTILLA.nombre} - ${PLANTILLA.subtitulo}`;

/**
 * Apunta los estilos, el título y el favicon del index.html a la plantilla del ambiente.
 * Se llama antes del bootstrap para que la app no se pinte con la plantilla equivocada.
 */
export function aplicarPlantilla(doc: Document): void {
    const oscuro = temaInicialOscuro();
    let anterior = doc.getElementById('layout-css');
    setHref(doc, 'theme-css', oscuro ? PLANTILLA.themeCssOscuro : PLANTILLA.themeCss);
    setHref(doc, 'layout-css', oscuro ? PLANTILLA.layoutCssOscuro : PLANTILLA.layoutCss);
    marcarTema(doc, oscuro);
    // Las hojas extra van justo después del layout y en el mismo orden en que se declaran
    PLANTILLA.estilosExtra.forEach((href, i) => {
        const id = `plantilla-css-${i}`;
        let link = doc.getElementById(id);
        if (!link) {
            link = doc.createElement('link');
            link.id = id;
            link.setAttribute('rel', 'stylesheet');
            link.setAttribute('href', href);
            anterior?.after(link);
        }
        anterior = link;
    });
    // Gancho para estilos globales propios de cada plantilla (p. ej. .plantilla-cbet en styles.scss)
    doc.documentElement.classList.add(`plantilla-${NOMBRE_PLANTILLA}`);
    doc.title = TITULO_APP;
    doc.querySelectorAll<HTMLLinkElement>('link[rel="icon"]').forEach(icon => icon.href = PLANTILLA.favicon);
}

// ---------- Modo oscuro ----------
// Sin preferencia guardada se sigue el tema del sistema; al usar el botón se guarda la elección.

export type PreferenciaTema = 'claro' | 'oscuro';

const CLAVE_TEMA = 'tema';

export function leerPreferenciaTema(): PreferenciaTema | null {
    try {
        const valor = localStorage.getItem(CLAVE_TEMA);
        return valor === 'claro' || valor === 'oscuro' ? valor : null;
    } catch {
        return null;
    }
}

export function guardarPreferenciaTema(preferencia: PreferenciaTema): void {
    try {
        localStorage.setItem(CLAVE_TEMA, preferencia);
    } catch {
        // sin almacenamiento (modo privado): el tema vale solo para esta pestaña
    }
}

export function sistemaEnOscuro(): boolean {
    return typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-color-scheme: dark)').matches;
}

export function temaInicialOscuro(): boolean {
    const preferencia = leerPreferenciaTema();
    return preferencia ? preferencia === 'oscuro' : sistemaEnOscuro();
}

/** Cambia las hojas de tema en caliente; la nueva reemplaza a la anterior cuando termina de cargar (sin parpadeo). */
export function cambiarTema(doc: Document, oscuro: boolean): void {
    reemplazarHoja(doc, 'theme-css', oscuro ? PLANTILLA.themeCssOscuro : PLANTILLA.themeCss);
    reemplazarHoja(doc, 'layout-css', oscuro ? PLANTILLA.layoutCssOscuro : PLANTILLA.layoutCss);
    marcarTema(doc, oscuro);
}

// .tema-oscuro en <html> permite ajustar estilos propios (styles.scss, login) al modo oscuro
function marcarTema(doc: Document, oscuro: boolean): void {
    doc.documentElement.classList.toggle('tema-oscuro', oscuro);
    doc.documentElement.style.colorScheme = oscuro ? 'dark' : 'light';
}

function reemplazarHoja(doc: Document, id: string, href: string): void {
    doc.getElementById(`${id}-nuevo`)?.remove();
    const actual = doc.getElementById(id);
    if (!actual || actual.getAttribute('href') === href) {
        return;
    }
    const nueva = actual.cloneNode(true) as HTMLLinkElement;
    nueva.id = `${id}-nuevo`;
    nueva.setAttribute('href', href);
    nueva.addEventListener('load', () => {
        actual.remove();
        nueva.id = id;
    }, {once: true});
    actual.after(nueva);
}

function setHref(doc: Document, id: string, href: string): void {
    const link = doc.getElementById(id) as HTMLLinkElement;
    if (link && link.getAttribute('href') !== href) {
        link.setAttribute('href', href);
    }
}
